import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Input,
  Rate,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faRotate } from '@fortawesome/pro-solid-svg-icons';
import {
  ConnectEnvironment,
  FeedbackCheckpoint,
  getUserFeedback,
  UserFeedbackRecord,
} from '../../services/connectFeedback';
import { colors } from '../../theme/colors';

const { Title, Text } = Typography;

const checkpointLabels: Record<FeedbackCheckpoint, string> = {
  first: 'First check-in',
  second: 'Second check-in',
  third: 'Third check-in',
  contextual: 'Contextual',
};

function formatTimestamp(value: string | null): string {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleString();
}

function storeReviewLabel(record: UserFeedbackRecord): string {
  if (record.status === 'dismissed' || !record.storeReviewAction) return '—';
  if (record.storeReviewAction === 'skipped') return 'Skipped';
  if (record.platform?.toLowerCase() === 'ios') return 'Opened App Store';
  if (record.platform?.toLowerCase() === 'android') return 'Opened Google Play';
  return 'Opened app store';
}

const UserFeedback: React.FC = () => {
  const [environment, setEnvironment] = useState<ConnectEnvironment>('dev');
  const [rating, setRating] = useState<number | undefined>();
  const [checkpoint, setCheckpoint] = useState<FeedbackCheckpoint | undefined>();
  const [userIdInput, setUserIdInput] = useState('');
  const [userId, setUserId] = useState('');
  const [cursor, setCursor] = useState<string | undefined>();
  const [cursorHistory, setCursorHistory] = useState<Array<string | undefined>>([]);

  const resetPagination = () => {
    setCursor(undefined);
    setCursorHistory([]);
  };

  const query = useQuery({
    queryKey: ['connectUserFeedback', environment, rating, checkpoint, userId, cursor],
    queryFn: () =>
      getUserFeedback({
        env: environment,
        limit: 50,
        cursor,
        rating,
        checkpoint,
        userId,
      }),
  });

  const columns: ColumnsType<UserFeedbackRecord> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 190,
      render: (value: string | null, record) => (
        <Space direction="vertical" size={2}>
          <Text style={{ color: colors.textPrimary }}>{value || '—'}</Text>
          <Text copyable type="secondary" style={{ fontSize: 12 }}>
            {record.userId}
          </Text>
          {record.coupleId && (
            <Text copyable type="secondary" style={{ fontSize: 12 }}>
              Couple: {record.coupleId}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 230,
      render: (value: string | null) => value || '—',
    },
    {
      title: 'Phone',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      width: 155,
      render: (value: string | null) => value || '—',
    },
    {
      title: 'Outcome',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (value: UserFeedbackRecord['status']) => (
        <Tag color={value === 'rated' ? 'green' : 'default'}>
          {value === 'rated' ? 'Rated' : 'Dismissed'}
        </Tag>
      ),
    },
    {
      title: 'Rating',
      dataIndex: 'rating',
      key: 'rating',
      width: 150,
      render: (value: number | null, record) =>
        record.status === 'dismissed' || value === null ? (
          '—'
        ) : (
          <Space size={8}>
            <Rate disabled value={value} count={5} style={{ fontSize: 14 }} />
            <Text style={{ color: colors.textMuted }}>{value}/5</Text>
          </Space>
        ),
    },
    {
      title: 'Feedback',
      dataIndex: 'comment',
      key: 'comment',
      render: (value: string | null, record) =>
        record.status === 'dismissed' ? (
          '—'
        ) : value ? (
          <div style={{ minWidth: 220, maxWidth: 520, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {value}
          </div>
        ) : (
          <Text type="secondary">No written feedback</Text>
        ),
    },
    {
      title: 'Checkpoint',
      dataIndex: 'checkpoint',
      key: 'checkpoint',
      width: 145,
      render: (value: FeedbackCheckpoint) => <Tag>{checkpointLabels[value] || value}</Tag>,
    },
    {
      title: 'Store action',
      key: 'storeReviewAction',
      width: 190,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Text>{storeReviewLabel(record)}</Text>
          {record.status === 'rated' && record.storeReviewActionAt && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {formatTimestamp(record.storeReviewActionAt)}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Submitted',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 185,
      render: formatTimestamp,
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 185,
      render: formatTimestamp,
    },
  ];

  const applyUserFilter = () => {
    setUserId(userIdInput.trim());
    resetPagination();
  };

  return (
    <div style={{ padding: 24, minHeight: '100vh', background: colors.bgBase }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ color: colors.textPrimary, margin: 0 }}>
          User Feedback
        </Title>
        <Text style={{ color: colors.textMuted }}>
          Individual responses submitted through the Ourora Connect rating widget.
        </Text>
      </div>

      <Card style={{ background: colors.bgBase, borderColor: colors.border }}>
        <Space wrap size={[12, 12]} style={{ marginBottom: 20 }}>
          <Segmented
            value={environment}
            onChange={(value) => {
              setEnvironment(value as ConnectEnvironment);
              resetPagination();
            }}
            options={[
              { label: 'Dev Server', value: 'dev' },
              { label: 'Production', value: 'prod' },
            ]}
          />
          <Select
            allowClear
            placeholder="All ratings"
            value={rating}
            style={{ width: 130 }}
            options={[1, 2, 3, 4, 5].map((value) => ({ label: `${value} stars`, value }))}
            onChange={(value) => {
              setRating(value);
              resetPagination();
            }}
          />
          <Select
            allowClear
            placeholder="All checkpoints"
            value={checkpoint}
            style={{ width: 170 }}
            options={Object.entries(checkpointLabels).map(([value, label]) => ({ value, label }))}
            onChange={(value) => {
              setCheckpoint(value);
              resetPagination();
            }}
          />
          <Input.Search
            allowClear
            value={userIdInput}
            placeholder="Filter by exact user ID"
            style={{ width: 280 }}
            onChange={(event) => {
              setUserIdInput(event.target.value);
              if (!event.target.value) {
                setUserId('');
                resetPagination();
              }
            }}
            onSearch={applyUserFilter}
          />
          <Button
            icon={<FontAwesomeIcon icon={faRotate} spin={query.isFetching} />}
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            Refresh
          </Button>
        </Space>

        {environment === 'prod' && (
          <Alert
            type="warning"
            showIcon
            message="Viewing production feedback"
            description="Comments may contain sensitive personal information. Use them only for product support and analysis."
            style={{ marginBottom: 16 }}
          />
        )}

        {query.isError ? (
          <Alert
            type="error"
            showIcon
            message={`Could not load ${environment.toUpperCase()} feedback`}
            description={(query.error as any)?.response?.data?.error || 'Please try again.'}
            action={<Button size="small" onClick={() => query.refetch()}>Retry</Button>}
          />
        ) : (
          <>
            <Table<UserFeedbackRecord>
              rowKey="responseId"
              columns={columns}
              dataSource={query.data?.feedback || []}
              loading={query.isLoading || query.isFetching}
              pagination={false}
              scroll={{ x: 1800 }}
              locale={{ emptyText: 'No feedback matches these filters.' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <Space>
                <Button
                  icon={<FontAwesomeIcon icon={faArrowLeft} />}
                  disabled={cursorHistory.length === 0 || query.isFetching}
                  onClick={() => {
                    const previous = cursorHistory[cursorHistory.length - 1];
                    setCursorHistory((history) => history.slice(0, -1));
                    setCursor(previous);
                  }}
                >
                  Previous
                </Button>
                <Button
                  disabled={!query.data?.nextCursor || query.isFetching}
                  onClick={() => {
                    setCursorHistory((history) => [...history, cursor]);
                    setCursor(query.data?.nextCursor || undefined);
                  }}
                >
                  Next
                  <FontAwesomeIcon icon={faArrowRight} />
                </Button>
              </Space>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default UserFeedback;
